import React, { useState } from 'react';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import {
	useImportMedicalProceduresByFieldOfCareMutation,
	useWorkplaceInsuranceCompaniesQuery,
} from '../../../../../../graphql/graphql';
import ManagerInsuranceCompaniesImportModal from '../ManagerInsuranceCompaniesImportModal/ManagerInsuranceCompaniesImportModal';
import { getWorkplaceInsuranceCompanies } from '../../../../Helpers/InsuranceCompanies/getWorkplaceInsuranceCompaniesIds';
import Loading from '../../../../../../Components/Loading';
import { useAppContext } from '../../../../../../Services/AppContext';
import styles from './ManagerInsuranceCompaniesCaseTypesImport.module.scss';
import ManagerInsuranceCompaniesImportCaseTypesModal from '../ManagerInsuranceCompaniesImportCaseTypes/ManagerInsuranceCompaniesImportCaseTypesModal';

interface Props {
	defaultAction: () => void;
	reloadData: () => void;
}

const ManagerInsuranceCompaniesCaseTypesImport = ({
	defaultAction,
	reloadData,
}: Props) => {
	const [isImportFromFileModalOpen, setIsImportFromFileModalOpen] =
		useState(false);
	const [isManualImportModalOpen, setIsManualImportModalOpen] = useState(false);

	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { displayGQLError } = useApiClient();
	const { workplaceId } = useAppContext();
	const [importMedicalProceduresByFieldOfCare] =
		useImportMedicalProceduresByFieldOfCareMutation();

	const {
		data: insuranceCompaniesData,
		isLoading: isLoadingInsuranceCompanies,
	} = useWorkplaceInsuranceCompaniesQuery({ workplaceId });

	const workplaceInsuranceCompanyCodes = getWorkplaceInsuranceCompanies(
		insuranceCompaniesData,
	);
	const hasInsuranceCompanies =
		workplaceInsuranceCompanyCodes && workplaceInsuranceCompanyCodes.length > 0;

	const handleNoInsuranceCompanies = () => {
		showNotificationAlert({
			content: 'component.errorMessages.noWorkplaceInsuranceCompanies',
		});
		defaultAction();
	};

	const handleImportFromFile = () => {
		if (!hasInsuranceCompanies) {
			handleNoInsuranceCompanies();
			return;
		}
		setIsImportFromFileModalOpen(true);
	};

	const handleManualImport = () => {
		if (!hasInsuranceCompanies) {
			handleNoInsuranceCompanies();
			return;
		}
		setIsManualImportModalOpen(true);
	};

	const handleImportByFieldOfCare = async () => {
		if (!hasInsuranceCompanies) {
			handleNoInsuranceCompanies();
			return;
		}

		const result = await importMedicalProceduresByFieldOfCare();

		if ('error' in result) {
			displayGQLError(result);
		} else {
			const data = result.data?.importMedicalProceduresByFieldOfCare;
			const successCount = data?.successCount ?? 0;
			const skipCount = data?.skipCount ?? 0;
			const message =
				skipCount === 0
					? t('component.caseType.importSuccess')
					: t('component.caseType.importSuccessWithCounts', {
							successCount: String(successCount),
							skipCount: String(skipCount),
					  });
			showNotificationSuccess({ content: message });
			reloadData();
		}
	};

	const handleCloseManualImportModal = () => {
		setIsManualImportModalOpen(false);
		reloadData();
	};

	if (isLoadingInsuranceCompanies) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<LinkButton
				title={'component.caseType.toLoadCaseTypes'}
				theme={Theme.BLUE}
				onClick={handleImportByFieldOfCare}
			/>
			<LinkButton
				title={'component.caseType.toAddManually'}
				theme={Theme.BLUE}
				onClick={handleManualImport}
			/>
			<LinkButton
				title={'component.caseType.toImport'}
				theme={Theme.BLUE}
				onClick={handleImportFromFile}
			/>
			{isImportFromFileModalOpen && (
				<ManagerInsuranceCompaniesImportCaseTypesModal
					isOpen
					onClose={() => setIsImportFromFileModalOpen(false)}
					refresh={reloadData}
				/>
			)}
			{isManualImportModalOpen && (
				<ManagerInsuranceCompaniesImportModal
					isOpen
					onClose={handleCloseManualImportModal}
				/>
			)}
		</div>
	);
};

export default ManagerInsuranceCompaniesCaseTypesImport;
